import SearchIcon from "@mui/icons-material/Search";
import { Grid2, InputAdornment, Paper, TextField } from "@mui/material";
import React from "react";
import {
	SearchShiftsParameters,
	ShiftPeriodNavigatorControl,
	TreePickerControlV2,
} from "../../../../../controls";
import {
	AssetsFilter,
	useAssets,
} from "../../../../../controls/asset-filter/AssetFilter";
import { useMesfRealtime } from "../../../../../hooks/useMesfRealtime";
import { Asset } from "../../../../../models/Asset";
import { TreeCode } from "../../../../../utils/transforms";

const customPeriodOptions = [
	{ value: "Day", label: "Day" },
	{ value: "4Days", label: "4 Days" },
	{ value: "Week", label: "Week" },
	{ value: "Month", label: "Month" },
	{ value: "Custom", label: "Custom" },
	{ value: "All", label: "All" },
];

const buildTreeAssets = (nodes: any) => {
	if (nodes === undefined) return;

	const idMapping = nodes.reduce((acc: any, el: any, i: number) => {
		acc[el?.id] = i;
		return acc;
	}, {});

	let root;

	nodes.forEach((el: any) => {
		if (el?.parentId === null) {
			root = el;
			return;
		}
		const parentEl = nodes[idMapping[el.parentId]];
		if (parentEl) {
			parentEl.children = [...(parentEl?.children || []), el];
		}
	});

	return root as any;
};

type Props = {
	withAssetFilter?: "tree" | "selector";
	filterAssets?: "area";
	quickFilter: string;
	onQuickFilterChange: (text: string) => void;
	assetId: number | null;
	setAssetId: (value: number | null) => void;
	shiftPeriod: SearchShiftsParameters;
	onChangeShiftPeriod: (
		value: SearchShiftsParameters,
		period: string | null,
	) => void;
	actualPeriod?: string | null;
};

export const Filters = ({
	withAssetFilter,
	filterAssets,
	quickFilter,
	onQuickFilterChange,
	assetId,
	setAssetId,
	shiftPeriod,
	onChangeShiftPeriod,
	actualPeriod,
}: Props) => {
	const handleShiftPeriodChange = (
		value: SearchShiftsParameters,
		period: string | null,
	) => {
		onChangeShiftPeriod(value, period);
	};

	useMesfRealtime({
		onReceiveMessage: (author, message) => {
			if (message.replaceAll("[", "").replaceAll("]", "") == "LB.GetEntries") {
			}
		},
	});

	const { data, isLoading } = useAssets();

	const rowsMapped = (Array.isArray(data) ? data : [])
		.filter((a) => filterAssets !== "area" || a.CanBeDefaultAsset)
		.map(
			(a: Asset) =>
				({
					id: a.AssetId,
					name: a.AssetName,
					parentId: a.ParentAssetId,
					isLeaf: a.IsLeaf,
					isActive: true,
				}) as TreeCode,
		);

	const assets = buildTreeAssets(rowsMapped || []);

	return (
		<Paper elevation={1} style={{ padding: 8 }}>
			<Grid2 container spacing={1}>
				<Grid2 size={{ xs: 12 }} alignItems="center" justifyContent="center">
					<ShiftPeriodNavigatorControl
						onChange={handleShiftPeriodChange}
						value={shiftPeriod}
						actualPeriod={actualPeriod}
						showShiftAndCrews={false}
						dateFormat="MM/DD/YYYY"
						periodOptions={customPeriodOptions}
					/>
				</Grid2>

				<Grid2 size={{ xs: 12, md: withAssetFilter ? 6 : 12 }}>
					<TextField
						fullWidth
						label="Search"
						variant="outlined"
						size="small"
						value={quickFilter}
						onChange={(event) => {
							onQuickFilterChange(event?.target.value);
						}}
						helperText={
							actualPeriod === "All" && !quickFilter.trim()
								? "Type a word to search across all shifts"
								: undefined
						}
						slotProps={{
							input: {
								startAdornment: (
									<InputAdornment position="start">
										<SearchIcon fontSize="small" />
									</InputAdornment>
								),
							},
						}}
					/>
				</Grid2>
				{withAssetFilter === "tree" && assets && (
					<Grid2
						size={{ xs: 12, md: 6 }}
						style={{
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						<TreePickerControlV2
							inputTitle="Asset"
							title="Choose Asset"
							dataSource={assets}
							value={assetId || 0}
							description=""
							onSelect={(value) => setAssetId(value ?? null)}
							selectBranch
							selectActive={false}
							selectInternal={false}
							showPath
							allowSearch
						/>
					</Grid2>
				)}
				{withAssetFilter === "selector" && (
					<Grid2
						size={{ xs: 12, md: 6 }}
						style={{
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						<AssetsFilter
							value={assetId ?? null}
							onChange={(newValue) => {
								setAssetId(newValue ?? null);
							}}
							filterAreaAssets={filterAssets === "area"}
							isFilter
						/>
					</Grid2>
				)}
			</Grid2>
		</Paper>
	);
};
